'use client';
import type { Grade, Team } from '@baseball-near-you/prisma';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@baseball-near-you/ui';
import { useSpecificTeam } from '@bb/lib/hooks/useSpecificTeam';
import { useTags } from '@bb/lib/hooks/useTags';
import { Plus } from 'lucide-react';
import { useParams } from 'next/navigation';
import posthog from 'posthog-js';
import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';
import { ProtectedAuthModal } from '../Auth/LoginModal/LoginModal';
import { AddTagsStep } from './AddTagsForm';
import { CreateReviewForm } from './CreateReviewForm';
import { GradeTeamForm } from './Grading/GradeTeamForm';

function ReviewModalTriggerButton(props: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) {
  return (
    <Button
      variant="outline"
      className="text-black"
      onClick={() => props.setOpen(!props.open)}
    >
      <Plus size={15} className="mr-2 " />
      Add Review
    </Button>
  );
}

export enum ReviewStep {
  Grade = 'grade',
  Message = 'message',
  Tags = 'tags',
}

export const ReviewContext = createContext<{
  team: Team;
  setIsOpen: (isOpen: boolean) => void;
  step: ReviewStep;
  setStep: (step: ReviewStep) => void;
  grades: Omit<Grade, 'id' | 'reviewId' | 'teamId'>;
  setGrades: (grades: Omit<Grade, 'id' | 'reviewId' | 'teamId'>) => void;
  selectedTags: number[];
  setSelectedTags: (selectedTags: number[]) => void;
}>({
  team: null as any,
  setIsOpen: () => {},
  step: ReviewStep.Grade,
  setStep: () => {},
  grades: {
    coaching: 0,
    culture: 0,
    facilities: 0,
    costValue: 0,
    competition: 0,
    communication: 0,
    recruiting: 0,
    playerDevelopment: 0,
  },
  setGrades: () => {},
  selectedTags: [],
  setSelectedTags: () => {},
});

export const useReviewContext = () => useContext(ReviewContext);

export function AddReviewModal(props: {
  teamSlug?: string;
  onSubmit?: (team: Team) => Promise<void>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const params = useParams();
  const { team, teamLoading } = useSpecificTeam(
    props.teamSlug ?? (params.slug as string)
  );

  const [step, setStep] = useState<ReviewStep>(ReviewStep.Grade);
  const [grades, setGrades] = useState<
    Omit<Grade, 'id' | 'reviewId' | 'teamId'>
  >({
    coaching: 0,
    culture: 0,
    facilities: 0,
    costValue: 0,
    competition: 0,
    communication: 0,
    recruiting: 0,
    playerDevelopment: 0,
  });
  const [selectedTags, setSelectedTags] = useState<number[]>([]);

  // preload the tags
  const { tags } = useTags();

  return (
    <ProtectedAuthModal
      modalTrigger={({ open, setOpen }) => (
        <ReviewModalTriggerButton open={open} setOpen={setOpen} />
      )}
      modal={
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <ReviewModalTriggerButton open={isOpen} setOpen={setIsOpen} />
          </DialogTrigger>
          {team && (
            <DialogContent>
              <ReviewContext.Provider
                value={{
                  team,
                  setIsOpen,
                  step,
                  setStep,
                  grades,
                  setGrades,
                  selectedTags,
                  setSelectedTags,
                }}
              >
                <DialogTitle>
                  Review{team?.name ? ` ${team.name}` : ''}
                </DialogTitle>
                <DialogDescription>
                  Help others discover the right team. Share your thoughts on
                  coaching, culture, and overall experience. Your feedback makes
                  a difference!
                </DialogDescription>
                {!!team && <AddReviewModalBody onSubmit={props.onSubmit} />}
              </ReviewContext.Provider>
            </DialogContent>
          )}
        </Dialog>
      }
    />
  );
}

export function AddReviewModalBody(props: {
  onSubmit?: (team: Team) => Promise<void>;
}) {
  const { setIsOpen, step, grades } = useReviewContext();

  return step === ReviewStep.Grade ? (
    <GradeTeamForm />
  ) : step === ReviewStep.Tags ? (
    <AddTagsStep />
  ) : step === ReviewStep.Message && !!grades ? (
    <CreateReviewForm
      onSubmit={async (team) => {
        await props.onSubmit?.(team);
        posthog.capture('review_submitted', {
          team_id: team.id,
          team_name: team.name,
        });
        setIsOpen(false);
      }}
    />
  ) : null;
}
